import {
  getPendingLogs,
  updateLogStatus,
  setLogImageUrl,
  getPendingWorkOrders,
  updateWorkOrderStatus,
  getPendingClients,
  updateClientStatus,
  getPendingSchedules,
  updateScheduleStatus,
  type PendingServiceLog,
  type PendingWorkOrder,
  type PendingClient,
} from './offline-queue';
import { uploadServiceLogImage } from './image-upload';
import { api } from '../lib/api';

// =============================================================================
// TYPES
// =============================================================================

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

// =============================================================================
// SYNC LOGIC
// =============================================================================

/**
 * Sync a single service log to the server
 */
interface SingleLogResult {
  success: boolean;
  imageUploadFailed: boolean;
}

async function syncSingleLog(log: PendingServiceLog): Promise<SingleLogResult> {
  let imageUploadFailed = false;
  try {
    await updateLogStatus(log.id, 'uploading');

    // Upload image first if exists
    let imageUrl = log.imageUrl;
    if (log.imageBlob && !imageUrl) {
      try {
        imageUrl = await uploadServiceLogImage(log.imageBlob, log.id);
        await setLogImageUrl(log.id, imageUrl);
      } catch (_error) {
        // Continue without image — caller surfaces this via SyncResult.errors
        imageUploadFailed = true;
      }
    }

    // Send to API using api module (includes auth headers)
    const response = await api.serviceLogs.create({
      clientId: log.clientId,
      employeeId: log.employeeId,
      timestamp: log.timestamp,
      missedService: log.missedService,
      serviceNotes: log.serviceNotes,
      latitude: log.latitude?.toString() ?? null,
      longitude: log.longitude?.toString() ?? null,
      imageUrl: imageUrl,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to sync service log');
    }

    await updateLogStatus(log.id, 'synced');
    return { success: true, imageUploadFailed };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    await updateLogStatus(log.id, 'failed', message);
    return { success: false, imageUploadFailed };
  }
}

/**
 * Sync all pending logs to the server
 * Returns summary of sync operation
 */
export async function syncPendingLogs(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  // Check if online
  if (!navigator.onLine) {
    result.success = false;
    result.errors.push('Device is offline');
    return result;
  }

  const pending = await getPendingLogs();

  if (pending.length === 0) {
    return result;
  }

  console.log(`Syncing ${pending.length} pending service logs...`);

  // Sync in order (oldest first)
  for (const log of pending) {
    // Skip logs that have failed too many times (will need manual intervention)
    if (log.retryCount >= 5) {
      result.failed++;
      result.errors.push(`Log ${log.id} exceeded retry limit`);
      continue;
    }

    const logResult = await syncSingleLog(log);
    if (logResult.success) {
      result.synced++;
      if (logResult.imageUploadFailed) {
        result.errors.push('photo_upload_failed');
      }
    } else {
      result.failed++;
      result.success = false;
    }

    // Small delay between requests to avoid overwhelming the server
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return result;
}

// =============================================================================
// WORK ORDERS SYNC
// =============================================================================

/**
 * Sync a single work order to the server
 */
async function syncSingleWorkOrder(wo: PendingWorkOrder): Promise<boolean> {
  try {
    await updateWorkOrderStatus(wo.id, 'uploading');

    let response;
    switch (wo.operation) {
      case 'create':
        response = await api.workorders.create(wo.data);
        break;
      case 'update':
        if (!wo.entityId) throw new Error('Missing entity ID for update');
        response = await api.workorders.update(wo.entityId, wo.data);
        break;
      case 'delete':
        // Delete not supported by API - mark as failed
        throw new Error('Delete operation not supported for work orders');
    }

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to sync work order');
    }

    await updateWorkOrderStatus(wo.id, 'synced');
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    await updateWorkOrderStatus(wo.id, 'failed', message);
    console.error(`Failed to sync work order ${wo.id}:`, message);
    return false;
  }
}

/**
 * Sync all pending work orders
 */
export async function syncPendingWorkOrders(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  if (!navigator.onLine) {
    result.success = false;
    result.errors.push('Device is offline');
    return result;
  }

  const pending = await getPendingWorkOrders();

  if (pending.length === 0) {
    return result;
  }

  console.log(`Syncing ${pending.length} pending work orders...`);

  for (const wo of pending) {
    if (wo.retryCount >= 5) {
      result.failed++;
      result.errors.push(`Work order ${wo.id} exceeded retry limit`);
      continue;
    }

    const success = await syncSingleWorkOrder(wo);
    if (success) {
      result.synced++;
    } else {
      result.failed++;
      result.success = false;
    }

    await new Promise(resolve => setTimeout(resolve, 100));
  }

  console.log(`Work orders sync complete: ${result.synced} synced, ${result.failed} failed`);
  return result;
}

// =============================================================================
// CLIENTS SYNC
// =============================================================================

/**
 * Sync a single client to the server
 */
async function syncSingleClient(client: PendingClient): Promise<boolean> {
  try {
    await updateClientStatus(client.id, 'uploading');

    let response;
    switch (client.operation) {
      case 'create':
        response = await api.clients.create(client.data);
        break;
      case 'update':
        if (!client.entityId) throw new Error('Missing entity ID for update');
        // API expects string ID, convert from number
        response = await api.clients.update(String(client.entityId), client.data);
        break;
      case 'delete':
        // Delete not supported by API - mark as failed
        throw new Error('Delete operation not supported for clients');
    }

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to sync client');
    }

    await updateClientStatus(client.id, 'synced');
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    await updateClientStatus(client.id, 'failed', message);
    console.error(`Failed to sync client ${client.id}:`, message);
    return false;
  }
}

/**
 * Sync all pending clients
 */
export async function syncPendingClients(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  if (!navigator.onLine) {
    result.success = false;
    result.errors.push('Device is offline');
    return result;
  }

  const pending = await getPendingClients();

  if (pending.length === 0) {
    return result;
  }

  console.log(`Syncing ${pending.length} pending clients...`);

  for (const client of pending) {
    if (client.retryCount >= 5) {
      result.failed++;
      result.errors.push(`Client ${client.id} exceeded retry limit`);
      continue;
    }

    const success = await syncSingleClient(client);
    if (success) {
      result.synced++;
    } else {
      result.failed++;
      result.success = false;
    }

    await new Promise(resolve => setTimeout(resolve, 100));
  }

  console.log(`Clients sync complete: ${result.synced} synced, ${result.failed} failed`);
  return result;
}

// =============================================================================
// SCHEDULES SYNC
// =============================================================================

/**
 * Sync all pending schedules
 *
 * NOTE: Schedule sync is currently NOT SUPPORTED because the API only has
 * specialized methods (saveDraft, publishWeek) and not generic CRUD operations.
 * Schedule changes should be made through the schedule grid UI which uses
 * the proper draft/publish workflow.
 *
 * TODO: Consider implementing schedule sync using saveDraft API if needed
 */
export async function syncPendingSchedules(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  const pending = await getPendingSchedules();

  if (pending.length > 0) {
    console.warn(`${pending.length} pending schedules cannot be synced - schedule sync not implemented`);
    result.errors.push('Schedule sync not supported - use schedule grid UI');

    // Mark all pending schedules as failed so they don't accumulate
    for (const schedule of pending) {
      await updateScheduleStatus(
        schedule.id,
        'failed',
        'Schedule sync not supported - use schedule grid UI'
      );
      result.failed++;
    }

    result.success = false;
  }

  return result;
}

// =============================================================================
// COMBINED SYNC
// =============================================================================

/**
 * Sync all pending mutations across all entity types
 */
export async function syncAllPending(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  if (!navigator.onLine) {
    result.success = false;
    result.errors.push('Device is offline');
    return result;
  }

  // Sync all entity types in parallel
  const [logsResult, workOrdersResult, clientsResult, schedulesResult] = await Promise.all([
    syncPendingLogs(),
    syncPendingWorkOrders(),
    syncPendingClients(),
    syncPendingSchedules(),
  ]);

  // Aggregate results
  result.synced = logsResult.synced + workOrdersResult.synced + clientsResult.synced + schedulesResult.synced;
  result.failed = logsResult.failed + workOrdersResult.failed + clientsResult.failed + schedulesResult.failed;
  result.errors = [
    ...logsResult.errors,
    ...workOrdersResult.errors,
    ...clientsResult.errors,
    ...schedulesResult.errors,
  ];
  result.success = logsResult.success && workOrdersResult.success && clientsResult.success && schedulesResult.success;

  console.log(`Total sync complete: ${result.synced} synced, ${result.failed} failed across all entity types`);
  return result;
}

/**
 * Set up automatic sync on network reconnection
 */
export function setupAutoSync(onSyncComplete?: (result: SyncResult) => void): () => void {
  const handleOnline = async () => {
    console.log('Network reconnected, syncing all pending mutations...');
    const result = await syncAllPending();
    onSyncComplete?.(result);
  };

  window.addEventListener('online', handleOnline);

  // Also check for pending mutations on load if online
  if (navigator.onLine) {
    setTimeout(async () => {
      const result = await syncAllPending();
      if (result.synced > 0 || result.failed > 0) {
        onSyncComplete?.(result);
      }
    }, 1000);
  }

  return () => {
    window.removeEventListener('online', handleOnline);
  };
}

/**
 * Check online status
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Subscribe to online/offline events
 */
export function subscribeToNetworkStatus(
  callback: (online: boolean) => void
): () => void {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}
